import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import css from './GoBackButton.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import Draggable from 'react-draggable';
import { useIntl } from 'react-intl';

const isSectionInViewport = sectionId => {
  const section = document.getElementById(sectionId);
  if (!section) {
    return false;
  }
  const { top, bottom } = section.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return bottom > 0 && top < viewportHeight;
};

/**
 * @param {boolean} desktopOnly - If true, only shows on desktop (>= 768px)
 * @param {string[]} darkSectionIds - Section ids that use a dark button background when in view
 * @param {string[]} lightSectionIds - Section ids that force the default red background when in view
 */
const GoBackButton = ({
  desktopOnly = false,
  darkSectionIds = [],
  lightSectionIds = [],
}) => {
  const history = useHistory();
  const intl = useIntl();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const buttonRef = useRef(null);
  const [isDarkBg, setIsDarkBg] = useState(false);

  const updateButtonTheme = useCallback(() => {
    const hasLightSection = lightSectionIds.some(isSectionInViewport);
    const hasDarkSection = darkSectionIds.some(isSectionInViewport);
    setIsDarkBg(hasDarkSection && !hasLightSection);
  }, [darkSectionIds, lightSectionIds]);

  useEffect(() => {
    if (darkSectionIds.length === 0 && lightSectionIds.length === 0) {
      setIsDarkBg(false);
      return undefined;
    }

    updateButtonTheme();

    window.addEventListener('scroll', updateButtonTheme, { passive: true });
    window.addEventListener('resize', updateButtonTheme, { passive: true });

    const pollTimer = setInterval(() => {
      const allIds = [...darkSectionIds, ...lightSectionIds];
      if (allIds.some(id => document.getElementById(id))) {
        updateButtonTheme();
        clearInterval(pollTimer);
      }
    }, 200);

    const stopPolling = setTimeout(() => clearInterval(pollTimer), 5000);

    return () => {
      window.removeEventListener('scroll', updateButtonTheme);
      window.removeEventListener('resize', updateButtonTheme);
      clearInterval(pollTimer);
      clearTimeout(stopPolling);
      setIsDarkBg(false);
    };
  }, [darkSectionIds, lightSectionIds, updateButtonTheme]);

  const hasInternalReferrer = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.history.length > 2;
  };

  const checkHistoryPresent = hasInternalReferrer();

  const handleGoBack = () => {
    if (history?.action === 'REPLACE') {
      history.goBack();
    } else {
      checkHistoryPresent && history?.action === 'PUSH' ? history.goBack() : history.push('/');
    }
  };

  const buttonClassName = [css.goBackButton, desktopOnly ? css.desktopOnly : null]
    .filter(Boolean)
    .join(' ');

  return (
    <Draggable disabled={isMobile} nodeRef={buttonRef}>
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        data-dark-bg={isDarkBg ? 'true' : 'false'}
        onClick={handleGoBack}
        aria-label="Go back to previous page"
      >
        <IconCard brand="back" />
        <span className={css.buttonText}>
          {intl.formatMessage({ id: 'GoBackButton.goBackButton' })}
        </span>
      </button>
    </Draggable>
  );
};

export default GoBackButton;
